import produce from 'immer'

import {
  DespatchAdviceItem,
  InvoiceItem,
  OrderResponseItem,
} from '../../../../data/models'

interface ConvertedItem
  extends Omit<
    OrderResponseItem | DespatchAdviceItem | InvoiceItem,
    'ConfirmationDeliveryDate' | 'DeliveryDate' | 'OrderDeliveryDate'
  > {
  ConfirmationDeliveryDate: Date
  DeliveryDate: Date
  HasAllowanceChargeSum?: boolean
  OrderDeliveryDate?: Date
}

/**
 * Converts a list of order items into a list of converted item objects.
 *
 * This function uses the Immer library to create a new array of converted item objects from
 * the provided array of order items. The conversion process involves:
 *
 * 1. Transforming date strings into Date objects for easier filtering and sorting in tables.
 * 2. Setting default values for product IDs and descriptions if they are missing.
 * 3. Adding a boolean property to indicate if the allowance charge sum is greater than 0.
 *
 * @param {OrderResponseItem[] | DespatchAdviceItem[] | InvoiceItem[] | undefined} items - The list of order items to be converted.
 * @returns {ConvertedItem[]} - The list of converted order items.
 */
export function convertOrderItems(
  items: OrderResponseItem[] | DespatchAdviceItem[] | InvoiceItem[] | undefined,
) {
  const convertedItems = produce(items, (draft: ConvertedItem[]) => {
    draft?.forEach(item => {
      // change the date from string to date so it is filterable
      item.ConfirmationDeliveryDate = new Date(item.ConfirmationDeliveryDate!)
      item.DeliveryDate = new Date(item.DeliveryDate)
      if (item.OrderDeliveryDate) {
        item.OrderDeliveryDate = new Date(item.OrderDeliveryDate)
      }

      // product id
      item.ConfirmationSellerProductId =
        item.ConfirmationSellerProductId ||
        item.SellerProductId ||
        item.BuyerProductId

      // product description
      item.ConfirmationSellerProductDescription =
        item.ConfirmationSellerProductDescription ||
        item.SellerProductDescription ||
        item.BuyerProductDescription

      // if the allownace charge sum is bigger than 0 give it the value true
      item.HasAllowanceChargeSum = item.ConfirmationAllowanceChargeSum
        ? true
        : false
    })
  })

  return convertedItems
}
